'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import Link from 'next/link';
import { Rss, LogIn, Globe, UserCheck, EyeOff, Info, Lock } from 'lucide-react';
import { api, FeedPost } from '@/lib/api';
import { PostCard } from '@/components/feed/PostCard';
import { CreatePost } from '@/components/feed/CreatePost';
import { Button } from '@/components/ui/Button';
import { ToastContainer } from '@/components/ui/Toast';
import { Navbar } from '@/components/layout/Navbar';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';

export default function FeedPage() {
  const { token } = useAuthStore();
  const { isAuthenticated } = useAuth();

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadPosts = useCallback(async (cursor?: string) => {
    if (cursor) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await api.feed.getPosts(cursor, token || undefined);
      setPosts((prev) => cursor ? [...prev, ...res.posts] : res.posts);
      setNextCursor(res.nextCursor);
      setHasMore(res.hasMore);
    } catch {
      // silently fail on initial load
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [token]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  function handleNewPost(post: FeedPost) {
    setPosts((prev) => [post, ...prev]);
  }

  function handleDelete(id: string) {
    setPosts((prev) => prev.filter((p) => p.id !== id));
  }

  function handleLikeChange(id: string, likedByMe: boolean, likeCount: number) {
    setPosts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, likedByMe, likeCount } : p))
    );
  }

  function handleCommentAdded(id: string, newCount: number) {
    setPosts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, commentCount: newCount } : p))
    );
  }

  return (
    <div className="min-h-screen bg-brand-black">
      <Navbar />
      {/* ── Main Content ────────────────────────────────────────────────────── */}
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-28 md:pb-16">
        {/* Page title */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-brand-white">Public Feed</h1>
          <p className="text-sm text-brand-gray mt-1">
            What strangers are thinking right now
          </p>
        </div>

        {/* Visibility info — members only */}
        <details className="mb-6 group">
          <summary className="flex items-center gap-2 text-xs text-brand-gray cursor-pointer select-none hover:text-brand-gray-light transition-colors list-none">
            <Info size={13} className="text-brand-lime/70 shrink-0" />
            Who can see what on the feed?
            <span className="ml-auto text-brand-gray/50 group-open:rotate-180 transition-transform text-[10px]">▼</span>
          </summary>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex flex-col gap-2 bg-brand-surface/60 border border-white/[0.06] rounded-xl p-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-brand-lime/10 flex items-center justify-center shrink-0">
                  <UserCheck size={13} className="text-brand-lime" />
                </div>
                <p className="text-xs font-semibold text-brand-white">Members only</p>
              </div>
              <p className="text-xs text-brand-gray leading-relaxed">
                The feed is exclusive to signed-in members. Create a free account to read and post.
              </p>
            </div>
            <div className="flex flex-col gap-2 bg-brand-surface/60 border border-white/[0.06] rounded-xl p-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <Globe size={13} className="text-blue-400" />
                </div>
                <p className="text-xs font-semibold text-brand-white">Members can post</p>
              </div>
              <p className="text-xs text-brand-gray leading-relaxed">
                Share thoughts with strangers. Your gender is shown; your username is optional.
              </p>
            </div>
            <div className="flex flex-col gap-2 bg-brand-surface/60 border border-white/[0.06] rounded-xl p-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                  <EyeOff size={13} className="text-purple-400" />
                </div>
                <p className="text-xs font-semibold text-brand-white">No real identity</p>
              </div>
              <p className="text-xs text-brand-gray leading-relaxed">
                Posts never show your email or IP. Skip a username and you appear as &ldquo;Anonymous&rdquo;.
              </p>
            </div>
          </div>
        </details>

        {/* Members-only gate */}
        {!isAuthenticated ? (
          <div className="relative mt-2">
            {/* Ghost post cards — blurred teaser */}
            <div className="flex flex-col gap-4 blur-sm pointer-events-none select-none" aria-hidden="true">
              {[
                { lines: ['w-full', 'w-3/4'], nameW: 'w-28', timeW: 'w-16' },
                { lines: ['w-full', 'w-1/2', 'w-5/6'], nameW: 'w-20', timeW: 'w-12' },
                { lines: ['w-full', 'w-2/3'], nameW: 'w-24', timeW: 'w-14' },
              ].map((card, i) => (
                <div
                  key={i}
                  className="bg-brand-surface/40 border border-white/[0.04] rounded-2xl p-5"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-brand-surface-2" />
                    <div className="flex flex-col gap-1.5">
                      <div className={`h-3 ${card.nameW} bg-brand-surface-2 rounded`} />
                      <div className={`h-2.5 ${card.timeW} bg-brand-surface-2 rounded`} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {card.lines.map((w, j) => (
                      <div key={j} className={`h-3 bg-brand-surface-2 rounded ${w}`} />
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-4">
                    <div className="h-2.5 w-10 bg-brand-surface-2 rounded" />
                    <div className="h-2.5 w-10 bg-brand-surface-2 rounded" />
                  </div>
                </div>
              ))}
            </div>

            {/* Sign-in overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-brand-black/10 via-brand-black/70 to-brand-black rounded-2xl">
              <div className="bg-brand-surface border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-2 text-center shadow-2xl">
                <div className="w-14 h-14 rounded-full bg-brand-lime/10 border border-brand-lime/20 flex items-center justify-center mx-auto mb-4">
                  <Lock size={24} className="text-brand-lime" />
                </div>
                <h2 className="text-xl font-bold text-brand-white">Members only</h2>
                <p className="text-sm text-brand-gray mt-2 mb-6 leading-relaxed">
                  Sign in to read what strangers are thinking right now.
                </p>
                <Link href="/login?redirect=/feed" className="block mb-3">
                  <Button fullWidth>
                    <LogIn size={14} />
                    Sign in to read
                  </Button>
                </Link>
                <Link
                  href="/login"
                  className="text-xs text-brand-gray hover:text-brand-white transition-colors"
                >
                  New here? Create a free account →
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Create post */}
            <div className="mb-6">
              <CreatePost onPost={handleNewPost} />
            </div>

            {/* Posts */}
            {loading ? (
              <div className="flex flex-col gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-brand-surface/40 border border-white/[0.04] rounded-2xl p-5 animate-pulse"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-brand-surface-2" />
                      <div className="flex flex-col gap-1.5">
                        <div className="h-3 w-24 bg-brand-surface-2 rounded" />
                        <div className="h-2.5 w-16 bg-brand-surface-2 rounded" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-brand-surface-2 rounded w-full" />
                      <div className="h-3 bg-brand-surface-2 rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
                <div className="w-16 h-16 rounded-full bg-brand-lime/10 flex items-center justify-center">
                  <Rss size={28} className="text-brand-lime" />
                </div>
                <div>
                  <p className="text-brand-white font-semibold">No posts yet</p>
                  <p className="text-sm text-brand-gray mt-1">Be the first to share something</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onDelete={handleDelete}
                    onLikeChange={handleLikeChange}
                    onCommentAdded={handleCommentAdded}
                  />
                ))}
              </div>
            )}

            {/* Load more */}
            {hasMore && !loading && (
              <div className="flex justify-center mt-8" ref={bottomRef}>
                <Button
                  variant="secondary"
                  onClick={() => loadPosts(nextCursor || undefined)}
                  loading={loadingMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      <ToastContainer />
    </div>
  );
}
